"use client";

// Maps scroll progress to the four big "moments" of the magic terminal story:
//   hero        — single Terminal Magique, code typing in
//   fragmented  — terminal explodes into 4 floating service screens (Phase 5)
//   crystals    — fragments converge into 3 pricing crystals (Phase 6)
//   final       — single Terminal Final pulsing on contact (Phase 7)
//
// The thresholds are deliberately wide so each state has time to breathe as
// the user scrolls. A small gap between bands lets the transitions overlap
// naturally with motion's AnimatePresence.

import { useEffect, useState } from "react";

export type SceneState = "hero" | "fragmented" | "crystals" | "final";

const HERO_END = 0.22;
const FRAG_END = 0.6;
const CRYS_END = 0.88;

function pickState(p: number): SceneState {
  if (p < HERO_END) return "hero";
  if (p < FRAG_END) return "fragmented";
  if (p < CRYS_END) return "crystals";
  return "final";
}

export function useSceneState(): SceneState {
  const [state, setState] = useState<SceneState>("hero");

  useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const max = Math.max(
          1,
          document.documentElement.scrollHeight - window.innerHeight
        );
        const p = window.scrollY / max;
        setState((prev) => {
          const next = pickState(p);
          return next === prev ? prev : next;
        });
      });
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      cancelAnimationFrame(raf);
    };
  }, []);

  return state;
}
